import { showNotificationGlobal } from "stores/useNotificationStore";
import type { RowGraphReexpand } from "../../exports";
import { useBoundStore } from "./stores/useAppStore";

const DATA_SERVICE = import.meta.env.VITE_APP_DATA_SERVICE as
  string | undefined;

const post = (path: string, body: string) =>
  fetch(new URL(path, DATA_SERVICE).toString(), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body,
  });

/** A refusal's JSON body, or nothing when it has none. */
async function refusal(res: Response): Promise<{
  error?: string;
  table_id?: string;
  table_ids?: string[];
}> {
  try {
    return JSON.parse(await res.text());
  } catch {
    return {};
  }
}

// One closure round trip at a time; triggers landing mid-flight coalesce into one re-run, which
// rebuilds its request from the current scene and so covers the whole burst.
let closeInFlight = false;
let closeRerun = false;

/** Fetches edges between rows already shown; additive. A 403 revokes the tables it names and re-closes (3 tries). */
export async function closeRowGraph(attempt = 0): Promise<void> {
  const app = useBoundStore.getState().clientApp;
  if (!app || !DATA_SERVICE) return;
  if (closeInFlight) {
    closeRerun = true;
    return;
  }
  let raw: string;
  try {
    raw = app.rowgraph_close_request();
  } catch {
    return;
  }
  if (raw === "null") return;

  closeInFlight = true;
  let retry = false;
  try {
    const res = await post("row-graph/close", raw);
    if (res.status === 403) {
      const body = await refusal(res);
      const ids =
        body.error === "not_discoverable" ? (body.table_ids ?? []) : [];
      if (ids.length > 0) {
        app.rowgraph_revoke_tables(JSON.stringify(ids));
        retry = attempt < 2;
      }
      return;
    }
    if (res.ok) {
      const text = await res.text();
      useBoundStore.getState().clientApp?.on_row_graph_close_received(text);
    }
  } catch {
    // Additive: the graph stays as it was.
  } finally {
    closeInFlight = false;
    if (retry) {
      void closeRowGraph(attempt + 1);
    } else if (closeRerun) {
      closeRerun = false;
      void closeRowGraph();
    }
  }
}

/** Expands `sourceNode` into `tableId`; `quiet` keeps an automatic re-run from toasting failures. */
export async function expand(
  sourceNode: string,
  tableId: string,
  bulk: boolean,
  quiet = false,
): Promise<void> {
  const notify: typeof showNotificationGlobal = (...args) => {
    if (!quiet) showNotificationGlobal(...args);
  };
  const app = useBoundStore.getState().clientApp;
  if (!app || !DATA_SERVICE) return;
  let request: { source_node: string; body: unknown };
  try {
    const raw = app.rowgraph_expand_request(sourceNode, tableId, bulk);
    if (raw === "null") {
      notify("This table can't be expanded yet.", "warning");
      return;
    }
    request = JSON.parse(raw);
  } catch {
    return;
  }
  try {
    const res = await post("row-graph/expand", JSON.stringify(request.body));
    if (res.status === 403) {
      const { table_id } = await refusal(res);
      if (table_id) app.rowgraph_revoke_tables(JSON.stringify([table_id]));
      notify("This table is no longer accessible.", "warning");
      return;
    }
    // A discover-only viewer reads released cuts, so a target with none can't be expanded.
    if (res.status === 409 && (await refusal(res)).error === "not_released") {
      notify(
        "This table has no released version yet — it can't be expanded.",
        "warning",
      );
      return;
    }
    if (!res.ok) {
      notify(`Couldn't expand (HTTP ${res.status}).`, "error");
      return;
    }
    const text = await res.text();
    useBoundStore
      .getState()
      .clientApp?.on_row_graph_expansion_received(
        request.source_node,
        tableId,
        text,
      );
  } catch {
    notify("Couldn't expand — request failed.", "error");
  }
}

// One re-run pass at a time; edits landing meanwhile coalesce into the next pass.
let reexpandPending = new Map<string, RowGraphReexpand>();
let reexpandInFlight = false;

/** Re-runs expansions an edit may have changed, so a row that starts matching reaches the graph. */
export async function reexpandRowGraph(
  expansions: RowGraphReexpand[],
): Promise<void> {
  for (const e of expansions) {
    reexpandPending.set(`${e.source_node}|${e.table_id}`, e);
  }
  if (reexpandInFlight) return;
  reexpandInFlight = true;
  try {
    while (reexpandPending.size > 0) {
      const batch = [...reexpandPending.values()];
      reexpandPending = new Map();
      for (const e of batch) {
        await expand(e.source_node, e.table_id, false, true);
      }
    }
  } finally {
    reexpandInFlight = false;
  }
}
